import { useNavigate } from 'react-router-dom';
import { themeClasses, Button } from '@member-portal/ui';
import {
  UserIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    title: 'Earn More',
    description: 'FHxD provides increased commission on every booking.',
    backgroundImage: '/images/images/colin-watts-4N6b6WCb97c-unsplash.jpg',
  },
  {
    title: 'Earn Faster',
    description: 'FHxD pays commission within 10 days after client checkout.',
    backgroundImage: '/images/images/gaynella-mckenney-ZEdTRK5MWLs-unsplash.jpg',
  },
  {
    title: 'World-Class Partner Collection',
    description: 'From icons to hidden gems, every participating partner has been carefully curated.',
    backgroundImage: '/images/images/giulia-squillace-Qt2Y4Q40Br4-unsplash.jpg',
  },
  {
    title: 'FHxD Benefits',
    description: 'FHxD provides benefits and amenities on every booking.',
    backgroundImage: '/images/images/maarten-fBpxIgj6Its-unsplash.jpg',
  },
  {
    title: 'Seamless Booking Experience',
    description: 'The FHxD reservations team is trained to deliver an unrivaled level of service.',
    backgroundImage: '/images/images/marvin-castelino-ff1NCMMnbqo-unsplash.jpg',
  },
  {
    title: 'Insider Information',
    description: 'Receive insider tips and inspiration as a registered advisor.',
    backgroundImage: '/images/images/planet-volumes-J0B_QzS-0JE-unsplash.jpg',
  },
];

const howItWorks = [
  {
    icon: UserIcon,
    step: '1',
    title: 'Advisor Sign Up',
    description:
      "Share your details as an accredited travel advisor, and we'll send your login credentials. Registration is complimentary, with no obligation.",
  },
  {
    icon: MagnifyingGlassIcon,
    step: '2',
    title: 'Explore',
    description: 'Log in to discover the FHxD world-class partner collection, benefits and details.',
  },
  {
    icon: DocumentTextIcon,
    step: '3',
    title: 'Request a Booking',
    description: 'Provide a few details and our FHxD team works with you to fulfill your request.',
  },
  {
    icon: BanknotesIcon,
    step: '4',
    title: 'Earn More, Faster',
    description:
      'FHxD provides increased commission on every booking, with payment issued within 10 days after client checkout.',
  },
];

function AdvisorOpportunitiesSections() {
  const navigate = useNavigate();

  return (
    <>
      <section className="py-24 bg-theme-bg-secondary">
        <div className={themeClasses.container}>
          <h2 className={`${themeClasses.title.h2} text-center mb-16`}>
            WHY FOUR HUNDRED BY DESIGN (FH<span className="lowercase">x</span>D)?
          </h2>
          <div className={themeClasses.grid.cols3}>
            {features.map((feature, index) => (
              <div key={index} className="relative overflow-hidden aspect-square">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${feature.backgroundImage})` }}
                />
                <div className="absolute inset-0 bg-black/50" />
                <div className="relative z-10 p-8 h-full flex flex-col justify-between">
                  <h3 className={`${themeClasses.title.h3} !text-white`}>{feature.title}</h3>
                  <p className={`${themeClasses.text.body} !text-white`}>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-theme-bg">
        <div className={themeClasses.container}>
          <h2 className={`${themeClasses.title.h2} text-center mb-16`}>How It Works</h2>
          <div className="max-w-2xl mx-auto">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative flex gap-8">
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center w-20 h-20 border border-theme-border">
                    <step.icon className="h-8 w-8 text-theme-text" />
                  </div>
                  {index < howItWorks.length - 1 && (
                    <div className="w-px h-full bg-theme-border flex-1 min-h-[4rem]" />
                  )}
                </div>
                <div className="flex-1 pb-12">
                  <p className={`${themeClasses.text.caption} mb-1`}>Step {step.step}</p>
                  <h3 className={`${themeClasses.title.h5} mb-3`}>{step.title}</h3>
                  <p className={themeClasses.text.muted}>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/images/ish-consul-i56w4ah3wAY-unsplash.jpg)' }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className={`${themeClasses.text.caption} text-white/80 uppercase tracking-widest mb-4`}>
            Exclusive Content
          </p>
          <h2 className={`${themeClasses.title.h3} text-white mb-6`}>INSIDER Magazine</h2>
          <p className={`${themeClasses.text.body} text-white/90 text-lg mb-8 max-w-2xl mx-auto`}>
            INSIDER e-magazine features insider knowledge and unique customer experiences
          </p>
          <button
            onClick={() => navigate('/insider')}
            className={`px-8 py-4 bg-white text-black ${themeClasses.text.button} hover:opacity-90 transition-colors duration-300`}
          >
            Explore INSIDER MAGAZINE
          </button>
        </div>
      </section>

      <section className="py-24 bg-theme-bg">
        <div className={themeClasses.container}>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={`${themeClasses.title.h2} mb-6`}>Meet Our Team</h2>
            <p className={`${themeClasses.text.body} text-theme-text-muted mb-8`}>
              Learn about the people behind Four Hundred by Design and our commitment to excellence
              in luxury travel.
            </p>
            <Button variant="secondary" onClick={() => navigate('/about')}>
              About Us
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}

export default AdvisorOpportunitiesSections;
